const CALLING_CODES = {
  AL: "355",
  AT: "43",
  BE: "32",
  CA: "1",
  CH: "41",
  DE: "49",
  DK: "45",
  ES: "34",
  FR: "33",
  GB: "44",
  IT: "39",
  NL: "31",
  NO: "47",
  SE: "46",
  US: "1",
  XK: "383",
};

export const CONTACT_PURPOSES = {
  business: [
    { id: "accounting", label: "Accounting questions" },
    { id: "document-request", label: "Document requests" },
    { id: "owner", label: "Owner or escalation" },
  ],
  client: [
    { id: "invoice-delivery", label: "Invoice delivery" },
    { id: "receivables", label: "Payment reminders" },
    { id: "owner", label: "Owner or escalation" },
  ],
};

export const MESSAGE_TEMPLATES = [
  { id: "missing-document", label: "Missing documents", audience: "business", purpose: "document-request" },
  { id: "missing-receipt", label: "Missing receipt or purchase invoice", audience: "business", purpose: "document-request" },
  { id: "filing-deadline", label: "Approaching filing deadline", audience: "business", purpose: "accounting" },
  { id: "payment-reminder", label: "Payment reminder", audience: "client", purpose: "receivables" },
  { id: "overdue-invoice", label: "Overdue invoice", audience: "client", purpose: "receivables" },
  { id: "general-follow-up", label: "General follow-up", audience: "all", purpose: "owner" },
];

export const createContact = (overrides = {}) => ({
  id: `contact-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
  name: "",
  role: "",
  email: "",
  phone: "",
  preferredChannel: "WhatsApp",
  language: "English",
  primary: false,
  primaryFor: [],
  status: "Active",
  ...overrides,
});

export const contactsFor = (record = {}) => {
  if (Array.isArray(record.contacts) && record.contacts.length) {
    return record.contacts.map((contact, index) => createContact({ ...contact, id: contact.id || `contact-${index + 1}`, primary: contact.primary ?? index === 0 }));
  }
  if (record.contact || record.email || record.phone) {
    return [createContact({ id: "primary-contact", name: record.contact || "", email: record.email || "", phone: record.phone || "", role: record.contactRole || "", primary: true })];
  }
  return [];
};

export const primaryContactFor = (record = {}) => {
  const contacts = contactsFor(record).filter((contact) => contact.status !== "Inactive");
  return contacts.find((contact) => contact.primary) || contacts[0] || null;
};

export const eligibleContactsFor = (record = {}, purpose = "", { requirePhone = false } = {}) => contactsFor(record)
  .filter((contact) => contact.status !== "Inactive" && (!requirePhone || contact.phone))
  .sort((left, right) => {
    const score = (contact) => (contact.primaryFor?.includes(purpose) ? 4 : 0) + (contact.primary ? 2 : 0) + (contact.preferredChannel === "WhatsApp" ? 1 : 0);
    return score(right) - score(left);
  });

export const contactForPurpose = (record = {}, purpose = "") => eligibleContactsFor(record, purpose)[0] || null;

export const prepareContacts = (contacts = [], countryCode = "") => {
  const useful = contacts
    .map((contact) => {
      const phone = contact.phone.trim();
      return { ...contact, name: contact.name.trim(), role: contact.role.trim(), email: contact.email.trim(), phone, normalizedPhone: normalizeWhatsAppPhone(phone, countryCode), status: contact.status || "Active", primaryFor: [...new Set(contact.primaryFor || [])] };
    })
    .filter((contact) => contact.name || contact.email || contact.phone);
  if (!useful.length) return [];
  const primaryId = useful.find((contact) => contact.primary)?.id || useful[0].id;
  const assignedPurposes = new Set();
  return useful.map((contact) => ({
    ...contact,
    primary: contact.id === primaryId,
    primaryFor: contact.primaryFor.filter((purpose) => {
      if (assignedPurposes.has(purpose)) return false;
      assignedPurposes.add(purpose);
      return true;
    }),
  }));
};

export const normalizeWhatsAppPhone = (value, countryCode = "") => {
  const input = String(value || "").trim();
  if (!input) return "";
  if (input.startsWith("+")) return input.replace(/\D/g, "");
  if (input.startsWith("00")) return input.slice(2).replace(/\D/g, "");
  const digits = input.replace(/\D/g, "");
  const callingCode = CALLING_CODES[countryCode];
  if (!callingCode) return digits;
  if (digits.startsWith(callingCode)) return digits;
  return `${callingCode}${digits.replace(/^0+/, "")}`;
};

const salutation = (contact, language) => language === "Albanian"
  ? `Pershendetje${contact?.name ? ` ${contact.name.split(/\s+/)[0]}` : ""}`
  : `Hello${contact?.name ? ` ${contact.name.split(/\s+/)[0]}` : ""}`;

const albanianDocumentRequest = (value) => String(value || "")
  .replace(/the following documents:/gi, "dokumentet ne vijim:")
  .replace(/the outstanding invoices and supporting receipts/gi, "faturat dhe kuponet mbeshtetese qe mungojne")
  .replace(/purchase invoices?/gi, "faturat e blerjes")
  .replace(/sales invoices?/gi, "faturat e shitjes")
  .replace(/bank statements?/gi, "pasqyrat bankare")
  .replace(/receipts?/gi, "kuponet mbeshtetese")
  .replace(/monthly reports?/gi, "raportet mujore")
  .replace(/quarterly filings?/gi, "deklarimet tremujore")
  .replace(/annual reports?/gi, "raportet vjetore")
  .replace(/tax documents?/gi, "dokumentet tatimore")
  .replace(/^the /i, "");

export const messageTemplatesFor = (request = {}) => {
  const audience = request.audience || (request.invoice || ["receivable", "payment-reminder", "overdue-invoice"].includes(request.type) ? "client" : "business");
  return MESSAGE_TEMPLATES.filter((template) => template.audience === "all" || template.audience === audience);
};

export const defaultMessageTemplate = (request = {}) => {
  if (request.templateId) return request.templateId;
  if (request.type === "receivable") return request.invoice?.status === "Overdue" ? "overdue-invoice" : "payment-reminder";
  return MESSAGE_TEMPLATES.some((template) => template.id === request.type) ? request.type : "general-follow-up";
};

export const buildWhatsAppMessage = ({ type, templateId, contact, language = contact?.language || "English", issuerName, businessName, invoice, period = "this month", documentName = "the outstanding accounting documents", deadline = "the agreed deadline", transactionDate = "the statement date", subject = "this matter" }) => {
  const hello = salutation(contact, language);
  const messageType = templateId || (type === "receivable" ? invoice?.status === "Overdue" ? "overdue-invoice" : "payment-reminder" : type) || "general-follow-up";
  if (language === "Albanian") {
    if (messageType === "missing-document") return `${hello}, po i pergatisim dokumentet e ${businessName || issuerName || "biznesit"} per ${period}. Ju lutem dergoni ${albanianDocumentRequest(documentName)} deri me ${deadline === "the agreed deadline" ? "afatin e dakorduar" : deadline}. Faleminderit.`;
    if (messageType === "missing-receipt") return `${hello}, per transaksionin e dates ${transactionDate} na mungon fatura ose kuponi mbeshtetes. Ju lutem dergojeni deri me ${deadline}, ne menyre qe regjistrimi te perfundoje sakte. Faleminderit.`;
    if (messageType === "filing-deadline") return `${hello}, afati per ${period} eshte ${deadline}. Ju lutem na dergoni dokumentet e mbetura sa me shpejt, qe deklarimi te pergatitet me kohe. Faleminderit.`;
    if (messageType === "general-follow-up") return `${hello}, po ju kontaktojme ne lidhje me ${subject}. Kur te keni mundesi, ju lutem na ktheni nje pergjigje. Faleminderit.`;
    if (messageType === "overdue-invoice") return `${hello}, fatura ${invoice?.invoiceNumber || invoice?.id || ""} nga ${issuerName || "ne"}, ne vlere ${invoice?.amount || ""}, ka kaluar afatin ${invoice?.due || invoice?.dueDate || ""}. Ju lutem na konfirmoni statusin e pageses ose na tregoni nese ju duhet nje kopje. Faleminderit.`;
    return `${hello}, nje rikujtim per faturen ${invoice?.invoiceNumber || invoice?.id || ""} nga ${issuerName || "ne"}, ne vlere ${invoice?.amount || ""}, me afat ${invoice?.due || invoice?.dueDate || ""}. Na tregoni nese pagesa eshte kryer ose nese ju duhet nje kopje. Faleminderit.`;
  }
  if (messageType === "missing-document") return `${hello}, we are preparing ${businessName || issuerName || "the business"}'s records for ${period}. Please send ${documentName} by ${deadline}. Thank you.`;
  if (messageType === "missing-receipt") return `${hello}, we are missing the supporting receipt or purchase invoice for the transaction dated ${transactionDate}. Please send it by ${deadline} so the record can be completed accurately. Thank you.`;
  if (messageType === "filing-deadline") return `${hello}, the filing deadline for ${period} is ${deadline}. Please send the remaining documents as soon as possible so the filing can be prepared on time. Thank you.`;
  if (messageType === "general-follow-up") return `${hello}, we are following up about ${subject}. Please reply when convenient. Thank you.`;
  if (messageType === "overdue-invoice") return `${hello}, invoice ${invoice?.invoiceNumber || invoice?.id || ""} from ${issuerName || "us"} for ${invoice?.amount || ""} passed its due date of ${invoice?.due || invoice?.dueDate || ""}. Please confirm the payment status or let us know if you need another copy. Thank you.`;
  return `${hello}, a quick reminder about invoice ${invoice?.invoiceNumber || invoice?.id || ""} from ${issuerName || "us"} for ${invoice?.amount || ""}, due ${invoice?.due || invoice?.dueDate || ""}. Please let us know if payment has already been arranged or if you need another copy. Thank you.`;
};

export const whatsappUrl = ({ contact, message, countryCode }) => {
  const phone = contact?.normalizedPhone || normalizeWhatsAppPhone(contact?.phone, countryCode);
  if (phone.length < 8) return "";
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};
